import React, { useEffect, useState, useRef, useCallback, type FC } from 'react';
import { graphql } from 'react-relay';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	CommentType,
	useCommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import {
	buildDefaultCommentsContentStatusFilters,
	findRootCommentForReply,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import {
	handleFooterCommentsNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import {
	ViewValues,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { getLogger } from '@confluence/logger';
import { usePageContentId } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { createReplyToCommentParams, type AuthorWithAccountId } from './replyToCommentHelper.ts';

import type {
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery as ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$data,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQueryVariables as ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$variables,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery_comments_PaginatedCommentList_nodes_Comment_location_FooterComment as FooterCommentLocation,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery_comments_PaginatedCommentList_nodes_Comment as ObjectCommentNode,
} from './__types__/ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery.ts';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	Comment,
	CommentReply,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { CommentQueryParamsListener } from './CommentQueryParamsListener.tsx';
// Type representing a comment with author field (both Comment and GraphQL query result have author)
type CommentWithAuthor = Pick<CommentData, 'author'> & {
	author?: {
		displayName?: string | null;
		accountId?: string | null;
	} | null;
};
const ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery = graphql`
	query ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				parentId
				author {
					displayName
					... on KnownUser {
						accountId
					}
				}
				location {
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
						}
					}
				}
			}
		}
	}
`;

const logger = getLogger('inline-comments:query-param-listener');

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

// Maximum wait time for scroll operations (in milliseconds)
const SCROLL_TIMEOUT_MS = 4000;

// Minimum time page must be stable before scrolling (in milliseconds)
const PAGE_STABILITY_DELAY_MS = 500;

// Wait for document height to stabilize (tables may have async layout)
const HEIGHT_STABILITY_CHECK_MS = 150;

// If there is a link to non-existent (probably deleted) comment, we don't want to show an error
const expectedErrorCodes = [404];

const isExpectedError = (error: ApolloErrorInterop) =>
	error.graphQLErrors.some((e) => expectedErrorCodes.includes(e?.extensions?.statusCode));

type ObjectCommentQueryParamsListenerProps = {
	hasLoadedObjectComments: boolean;
	allObjectCommentsRendered: boolean;
	commentBatchRendered?: boolean;
	commentBatchCount?: number;
	setForceLoadComments: (value: React.SetStateAction<boolean>) => void;
};

const ObjectCommentQueryParamsListenerComponent: FC<ObjectCommentQueryParamsListenerProps> = ({
	hasLoadedObjectComments,
	allObjectCommentsRendered,
	commentBatchRendered,
	commentBatchCount,
	setForceLoadComments,
}) => {
	const [hasScrolledToFocusedComment, setHasScrolledToFocusedComment] = useState<boolean>(false);
	const [hasScrolledToReplyComment, setHasScrolledToReplyComment] = useState<boolean>(false);
	const mutationObserverRef = useRef<MutationObserver | null>(null);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
	const lastMutationTimeRef = useRef<number>(0);

	// Helper function to clear scroll timeout/callback
	const clearScrollTimeout = () => {
		if (scrollTimeoutRef.current !== null) {
			if (typeof scrollTimeoutRef.current === 'number') {
				// requestIdleCallback ID
				if (
					typeof window !== 'undefined' &&
					'cancelIdleCallback' in window &&
					typeof (window as any).cancelIdleCallback === 'function'
				) {
					(window as any).cancelIdleCallback(scrollTimeoutRef.current);
				}
			} else {
				// setTimeout ID
				clearTimeout(scrollTimeoutRef.current);
			}
			scrollTimeoutRef.current = null;
		}
	};

	// Helper function to clean up MutationObserver
	const cleanupMutationObserver = () => {
		if (mutationObserverRef.current) {
			mutationObserverRef.current.disconnect();
			mutationObserverRef.current = null;
		}
	};

	// Helper function to clean up both observer and timeout (only if they were set up)
	const cleanupScrollResources = useCallback(() => {
		if (mutationObserverRef.current) {
			cleanupMutationObserver();
		}
		if (scrollTimeoutRef.current !== null) {
			clearScrollTimeout();
		}
	}, []); // Empty deps: only uses refs which don't change

	const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();
	const [contentId] = usePageContentId();
	const pageMode = useGetPageMode();
	const processedCommentsData = useProcessedCommentsDataState();
	const { commentsDataMap } = useCommentsDataState();

	const focusedCommentId = useFocusedCommentIdQueryParam();
	const replyToComment = useReplyToCommentQueryParam();

	const commentId = focusedCommentId || replyToComment;

	// NEW FLOW: Use useCommentById when experiment is enabled
	const { comment, error: commentError } = useCommentById({
		commentId: commentId || '',
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
		options: {
			skip: !commentId || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		},
	});

	// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)
	const { data, error } = useQueryInterop<
		ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$data,
		ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$variables
	>(ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery, {
		variables: {
			commentId: commentId!,
		},
		skip: !commentId || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});
	if (error && !isExpectedError(error)) {
		logger.error`An error occurred when querying the focused object comment status; ${error}`;
	}

	let isResolved: boolean | undefined = undefined;
	let parentId: string | null | undefined = undefined;
	// Extract resolved status and parentId from either new or old flow
	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		// NEW FLOW: Extract from useCommentById
		if (comment && !commentError) {
			const location = comment.location as FooterCommentLocation;
			isResolved = Boolean(location?.commentResolveProperties?.resolved);
			parentId = comment.parentId;
		}
	} else {
		// OLD FLOW: Extract from GraphQL query
		// If the commentId is not present, error is defined and isResolved will be undefined
		if (data && !error) {
			const location = data?.comments?.nodes?.[0]?.location as FooterCommentLocation;
			isResolved = Boolean(location?.commentResolveProperties?.resolved);
			parentId = (
				data?.comments?.nodes?.[0] as ObjectCommentNode & {
					parentId?: string | null;
				}
			)?.parentId;
		}
	}

	// Get comment data for replyToComment handling (with author)
	const rawCommentForReply: Comment | CommentReply | ObjectCommentNode | null | undefined = expVal(
		'cc_comments_new_comments_data_manager',
		'isEnabled',
		false,
	)
		? comment
		: data?.comments?.nodes?.[0] || null;

	// Handle scrolling to focused comment or replyToComment after the data is loaded
	useEffect(() => {
		// Determine if we have data from new or old flow
		const hasData = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? comment && !commentError
			: data && !error;

		// Update currentlySelectedThreadKey to root comment ID when data becomes available
		// This runs even after scroll has happened to ensure correct thread highlighting
		if (
			parentId &&
			(hasScrolledToFocusedComment || hasScrolledToReplyComment) &&
			expValEquals('confluence_comment_deeplink_thread_selection', 'isEnabled', true)
		) {
			let rootCommentId: string | undefined;
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				if (processedCommentsData.isDataProcessed && processedCommentsData.allComments.length > 0) {
					const rootComment = findRootCommentForReply(processedCommentsData.allComments, parentId);
					rootCommentId = rootComment?.id;
				}
			} else {
				const legacyFooterComments = Object.values(commentsDataMap.general);
				if (legacyFooterComments.length > 0) {
					const rootComment = findRootCommentForReply(
						legacyFooterComments as unknown as Comment[],
						parentId,
					);
					rootCommentId = rootComment?.id;
				}
			}
			if (rootCommentId && rootCommentId !== parentId) {
				setCurrentlySelectedThreadKey(rootCommentId);
			}
		}

		// Early return if comment type is INLINE - this component only handles footer/object comments
		if (
			hasData &&
			commentId &&
			expValEquals('confluence_comment_deeplink_queryparam_selection', 'isEnabled', true)
		) {
			const location = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? comment?.location
				: data?.comments?.nodes?.[0]?.location;

			if (location && 'type' in location && location.type === 'INLINE') {
				return;
			}
		}
		if (
			// Note that if the commentId is not found, the query will return an error and we don't
			// have to scroll to the comment
			commentId &&
			hasData &&
			hasLoadedObjectComments &&
			allObjectCommentsRendered &&
			!hasScrolledToFocusedComment &&
			!hasScrolledToReplyComment
		) {
			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				setCurrentlySelectedThreadKey(commentId, CommentType.GENERAL);
				if (focusedCommentId) {
					setHasScrolledToFocusedComment(true);
				} else {
					setHasScrolledToReplyComment(true);
				}
			} else {
				const commentElementId = `footer-comment-thread-${commentId}-container`;
				const replyCommentElementId = `footer-comment-thread-${commentId}-replies`;

				const commentElement = document.getElementById(commentElementId);
				const replyCommentElement = document.getElementById(replyCommentElementId);
				const element = commentElement || replyCommentElement;
				// When experiment is enabled, select the root thread (for replies) or the comment itself
				// This ensures the entire thread is highlighted when deep-linking to a reply
				let threadToBeSelected = commentId;
				if (
					parentId &&
					expValEquals('confluence_comment_deeplink_thread_selection', 'isEnabled', true)
				) {
					// Try to find the root comment ID from available data sources
					let rootCommentId: string | undefined;
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (
							processedCommentsData.isDataProcessed &&
							processedCommentsData.allComments.length > 0
						) {
							const rootComment = findRootCommentForReply(
								processedCommentsData.allComments,
								parentId,
							);
							rootCommentId = rootComment?.id;
						}
					} else {
						const legacyFooterComments = Object.values(commentsDataMap.general);
						if (legacyFooterComments.length > 0) {
							const rootComment = findRootCommentForReply(
								legacyFooterComments as unknown as Comment[],
								parentId,
							);
							rootCommentId = rootComment?.id;
						}
					}
					threadToBeSelected = rootCommentId ?? parentId;
				}
				// Use unified navigation for scrolling
				if (commentId) {
					if (expValEquals('confluence_comment_deeplink_delay_scroll', 'isEnabled', true)) {
						if (replyToComment) {
							setCurrentlySelectedThreadKey(threadToBeSelected);

							// Get author information from comment data
							const commentWithAuthor = rawCommentForReply as unknown as CommentWithAuthor;
							const author = commentWithAuthor?.author;

							setReplyToCommentId(
								createReplyToCommentParams(
									replyToComment,
									LocationValue.FOOTER,
									author as AuthorWithAccountId | null | undefined,
									expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true),
								),
							);
							setHasScrolledToReplyComment(true);
							return;
						}
						// Use MutationObserver to watch for when the element is ready
						// Clean up any existing observer first
						cleanupMutationObserver();

						const getDocumentScrollHeight = () =>
							Math.max(
								document.documentElement?.scrollHeight ?? 0,
								document.body?.scrollHeight ?? 0,
							);

						const scrollToComment = () => {
							const element =
								document.getElementById(commentElementId) ||
								document.getElementById(replyCommentElementId);
							if (element && fg('confluence_comment_footer_scrolling_fix')) {
								void element.offsetTop;
								void getDocumentScrollHeight();
							}
							handleFooterCommentsNavigation({
								scrollBehavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
								commentId,
								navigationContext: parentId ? NavigationContext.REPLY : undefined,
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(threadToBeSelected);
							cleanupMutationObserver();
						};

						// Perform scroll; when FG on: double rAF + force reflow for correct layout
						const performScrollAndCleanup = () => {
							clearScrollTimeout();
							if (fg('confluence_comment_footer_scrolling_fix')) {
								requestAnimationFrame(() => {
									requestAnimationFrame(scrollToComment);
								});
							} else {
								scrollToComment();
							}
						};

						// When FG on: check document height stable (tables may have async layout)
						const checkHeightStabilityThenScroll = () => {
							if (!fg('confluence_comment_footer_scrolling_fix')) {
								performScrollAndCleanup();
								return;
							}
							const h1 = getDocumentScrollHeight();
							scrollTimeoutRef.current = setTimeout(() => {
								scrollTimeoutRef.current = null;
								const h2 = getDocumentScrollHeight();
								if (h1 === h2) {
									performScrollAndCleanup();
								} else {
									scheduleScroll();
								}
							}, HEIGHT_STABILITY_CHECK_MS);
						};

						// Schedule scroll when page is idle/stable
						const scheduleScroll = () => {
							clearScrollTimeout();

							// Track when we detected the element is ready
							lastMutationTimeRef.current = Date.now();

							// Use requestIdleCallback if available, otherwise fallback to setTimeout
							if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
								scrollTimeoutRef.current = (window as any).requestIdleCallback(() => {
									const checkStabilityAndScroll = () => {
										const timeSinceLastMutation = Date.now() - lastMutationTimeRef.current;
										if (timeSinceLastMutation >= PAGE_STABILITY_DELAY_MS) {
											// Mutations stable - when FG on: check height stable, then scroll
											checkHeightStabilityThenScroll();
										} else {
											// Not stable yet - wait and re-check (don't scroll blindly)
											scrollTimeoutRef.current = setTimeout(() => {
												scrollTimeoutRef.current = null;
												checkStabilityAndScroll();
											}, PAGE_STABILITY_DELAY_MS - timeSinceLastMutation);
										}
									};
									checkStabilityAndScroll();
								});
							} else {
								// Fallback for browsers without requestIdleCallback (e.g., Safari)
								scrollTimeoutRef.current = setTimeout(() => {
									scrollTimeoutRef.current = null;
									checkHeightStabilityThenScroll();
								}, SCROLL_TIMEOUT_MS);
							}
						};

						// Check if element exists and is ready (has content and is visible)
						const getElementAndCheckReady = () => {
							const foundElement =
								document.getElementById(commentElementId) ||
								document.getElementById(replyCommentElementId);
							if (!foundElement) {
								return { element: null, isReady: false };
							}
							const rect = foundElement.getBoundingClientRect();
							const elementIsReady =
								foundElement.offsetHeight > 0 &&
								foundElement.offsetWidth > 0 &&
								rect.height > 0 &&
								rect.width > 0;
							return { element: foundElement, isReady: elementIsReady };
						};

						const { element: currentElement, isReady } = getElementAndCheckReady();

						// If element exists and is already ready, schedule scroll with delay
						if (currentElement && isReady) {
							scheduleScroll();
						} else {
							// Set up MutationObserver to watch for when element appears or becomes ready
							mutationObserverRef.current = new MutationObserver(() => {
								// Update last mutation time when DOM changes
								lastMutationTimeRef.current = Date.now();
								const { element: foundElement, isReady: foundIsReady } = getElementAndCheckReady();
								if (foundElement && foundIsReady) {
									scheduleScroll();
								}
							});

							// If element exists, observe it and its parent
							if (currentElement) {
								mutationObserverRef.current.observe(currentElement, {
									childList: true,
									subtree: true,
									attributes: true,
									attributeFilter: ['style', 'class'],
								});

								const parentContainer = currentElement.parentElement;
								if (parentContainer) {
									mutationObserverRef.current.observe(parentContainer, {
										childList: true,
										subtree: true,
									});
								}
							} else {
								// If element doesn't exist yet, observe document.body to detect when it appears
								mutationObserverRef.current.observe(document.body, {
									childList: true,
									subtree: true,
								});
							}

							// Fallback timeout in case MutationObserver doesn't detect changes
							setTimeout(() => {
								if (mutationObserverRef.current) {
									checkHeightStabilityThenScroll();
								}
							}, SCROLL_TIMEOUT_MS);
						}
					} else {
						// Experiment disabled - scroll immediately if element exists
						if (element) {
							handleFooterCommentsNavigation({
								scrollBehavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
								commentId,
								navigationContext: parentId ? NavigationContext.REPLY : undefined,
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(threadToBeSelected);
						}
					}
				}
			}
		}
	}, [
		data,
		error,
		comment,
		commentError,
		hasLoadedObjectComments,
		commentId,
		focusedCommentId,
		replyToComment,
		allObjectCommentsRendered,
		commentBatchRendered,
		commentBatchCount,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
		isResolved,
		showCommentsPanel,
		hasScrolledToFocusedComment,
		hasScrolledToReplyComment,
		rawCommentForReply,
		parentId,
		processedCommentsData,
		commentsDataMap,
	]);

	// Reset scroll tracking when focusedCommentId or replyToComment changes
	useEffect(() => {
		setHasScrolledToFocusedComment(false);
		setHasScrolledToReplyComment(false);
		// Clean up scroll resources when comment ID changes (only if they were set up)
		cleanupScrollResources();
	}, [focusedCommentId, replyToComment, cleanupScrollResources]);

	// Clean up MutationObserver and timeout on unmount
	useEffect(() => {
		return () => {
			cleanupScrollResources();
		};
	}, [cleanupScrollResources]);

	// Force load comments if focusedCommentId or replayToComment is present
	useEffect(() => {
		if ((focusedCommentId || replyToComment) && !hasLoadedObjectComments) {
			setForceLoadComments(true);
		}
	}, [focusedCommentId, replyToComment, hasLoadedObjectComments, setForceLoadComments]);

	return null;
};

const withObjectCommentQueryParamUnification =
	(
		Component: FC<ObjectCommentQueryParamsListenerProps>,
	): FC<ObjectCommentQueryParamsListenerProps> =>
	(props) => {
		if (fg('confluence_comment_param_unification')) {
			return <CommentQueryParamsListener {...props} />;
		}
		return <Component {...props} />;
	};

export const ObjectCommentQueryParamsListener: React.FC<ObjectCommentQueryParamsListenerProps> =
	withObjectCommentQueryParamUnification(ObjectCommentQueryParamsListenerComponent);
